import { ReactNode, useCallback, useEffect } from "react";

import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { Stack, Box, xcss, media } from "@atlaskit/primitives";

import { useAnalyticsClient } from "../../../../../analytics/wac-analytics/analytics-client";

import HeroCompanies from "./hero-companies";
import HeroTabs from "./hero-tabs";

const messages = {
    heading: {
        id: "wacJiraProductDiscoveryPage.hero-heading",
        defaultMessage: "From ideas to impact, <span>build what matters</span>"
    },
    subheading: {
        id: "wacJiraProductDiscoveryPage.hero-subheading",
        defaultMessage:
            "Capture and prioritize ideas and align everyone with roadmaps – all in Jira."
    },
    tabs: {
        label: "Product tour"
    }
};

const JpdUseCaseProductTourOnWac = () => {
    const analyticsClient = useAnalyticsClient();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const fireExperimentScreenEvent = useCallback(() => {
        analyticsClient &&
            createAnalyticsEvent({
                event: "success",
                action: "rendered",
                eventType: "operational",
                actionSubject: "feature",
                attributes: {
                    experiment: "jpd_use_case_product_tour_on_wac"
                }
            }).fire();
    }, [createAnalyticsEvent, analyticsClient]);
    useEffect(() => {
        fireExperimentScreenEvent();
    }, [fireExperimentScreenEvent]);
    return (
        <Box xcss={HeroContainerStyle}>
            <Stack alignInline="center">
                <Box xcss={HeroHeadingWrapperStyles}>
                    <Box as="h1" xcss={HeroHeadingStyles}>
                        <FormattedMessage
                            id={messages.heading.id}
                            defaultMessage={messages.heading.defaultMessage}
                            values={{
                                span: (text: ReactNode) => (
                                    <Box as="span" xcss={HeroHeadingSpanStyles}>
                                        {text}
                                    </Box>
                                )
                            }}
                        />
                    </Box>
                    <svg
                        width="96"
                        height="96"
                        viewBox="0 0 96 96"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        css={HeroHeadingFigureStyles}
                        aria-hidden="true"
                    >
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M33.58 47.4998L39.92 41.1598L33.58 34.8198L38.87 29.5298H51.56L61.08 20.0098H75.88V34.8098L66.36 44.3298V57.0198L61.07 62.3098L54.73 55.9698L48.39 62.3098L42.58 56.4998L49.54 49.5398L46.37 46.3698L39.41 53.3298L33.59 47.5098L33.58 47.4998ZM68.47 32.6998C67.01 34.1598 64.64 34.1598 63.18 32.6998C61.72 31.2398 61.72 28.8698 63.18 27.4098C64.64 25.9498 67.01 25.9498 68.47 27.4098C69.93 28.8698 69.93 31.2398 68.47 32.6998Z"
                            fill="#1868DB"
                        />
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M50.44 65.0098C44.07 71.3798 35.12 75.6498 20.26 75.3298C19.93 60.4698 24.2 51.5298 30.58 45.1498C36.42 39.3098 45.51 38.0498 54.67 40.9098C57.53 50.0698 56.28 59.1598 50.43 64.9998L50.44 65.0098Z"
                            fill="#FCA700"
                        />
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M55.31 56.53L54.74 55.96L48.39 62.3L42.58 56.49L49.54 49.53L46.37 46.36L39.41 53.32L33.59 47.5L39.93 41.15L39.06 40.28C43.79 38.96 49.22 39.21 54.68 40.91C56.38 46.37 56.63 51.8 55.31 56.53Z"
                            fill="#09326C"
                        />
                        <path
                            d="M49.4172 69.1946C49.3903 69.1942 49.356 69.2037 49.3191 69.1959C49.3858 69.9193 48.6315 70.3161 48.4543 70.9613C48.5763 71.0003 48.4494 70.9884 48.4494 70.9884C48.59 71.5164 48.1902 72.0452 48.1908 72.0495C47.781 73.3876 46.8954 75.0726 46.5904 76.5752L46.6166 76.5713L46.6367 76.5859C46.8401 76.5119 46.6069 76.9591 46.5504 76.8489C45.9798 77.5215 45.3319 79.0616 45.3508 79.9149C45.5083 79.9531 45.4581 80.1273 45.4111 80.0816C44.9461 80.26 42.1514 86.4373 44.0706 85.4293L44.2196 85.5303C44.443 85.168 44.6265 84.8687 45.0931 84.5802C45.088 84.5151 45.1242 84.5185 44.9592 84.4595C44.9423 84.4357 45.9503 82.4911 45.9703 82.5057C46.3833 82.5501 46.4998 81.3124 46.4998 81.3124C48.4481 79.927 48.2583 76.0126 50.1086 74.4178L50.0311 74.4073C50.0574 74.4034 50.0435 74.3396 50.1353 74.3567C49.9766 74.0685 50.0021 73.4852 50.0021 73.4852C50.0021 73.4852 50.5832 71.9769 50.2496 71.4116L50.9941 70.0416C50.9941 70.0416 51.0944 69.3902 50.8638 69.0994C50.7203 68.9143 50.5276 68.7584 50.3268 68.6695C49.9241 68.4832 49.5013 68.5544 49.4253 69.189L49.4172 69.1946Z"
                            fill="#101214"
                        />
                        <path
                            d="M30.0221 64.7202C29.9983 64.7106 29.967 64.7108 29.9333 64.6938C29.9888 64.949 29.8111 65.0763 29.6308 65.1863C29.4556 65.3 29.2346 65.3766 29.1351 65.5493C29.2402 65.626 29.3084 65.6949 29.2503 65.664L29.1282 65.5635C29.2167 65.9807 28.7064 65.7619 28.9444 66.315L28.8085 66.1507C28.3017 66.8622 27.2243 67.4912 26.5741 68.3204L26.5941 68.335L26.606 68.3552C26.7811 68.4215 27.2843 68.3208 27.2843 68.3208C27.5037 69.1401 26.6869 68.7515 26.4201 69.0017L26.2929 68.8361C26.422 68.7424 26.4747 68.5853 26.4682 68.4809C25.8469 68.5991 24.889 69.2806 24.6575 69.8899C24.7677 70.001 24.6856 69.6268 25.0431 69.991C24.9359 70.0815 24.8732 70.2312 24.7646 70.3131C24.5485 70.0597 24.6835 70.0968 24.6572 70.0392C24.4552 69.9725 23.3379 70.6337 22.5709 71.489C22.384 71.7054 22.2147 71.9192 22.0839 72.1229C21.9589 72.3345 21.8763 72.5311 21.8386 72.6991C21.7675 73.0345 21.9179 73.2362 22.3777 73.2341L22.4948 73.3617C22.7374 73.1897 22.9338 73.0378 23.3761 73.0383C23.3822 72.9891 23.4123 73.011 23.2833 72.8939C23.2707 72.8694 23.2457 72.8205 23.19 72.776C22.8726 72.5024 23.062 72.5447 23.0831 72.5064C23.5521 72.9596 23.7279 72.8194 23.8048 72.5535C23.8866 72.2911 23.8919 71.904 23.9543 71.8728C24.4064 72.3021 24.1495 71.7123 24.3885 71.8482C24.4004 71.8684 24.4725 71.9938 24.4844 72.014C24.7936 72.2625 24.9186 70.6899 25.1828 71.4806C27.016 71.6136 28.0037 68.9872 29.9285 68.7423L29.8684 68.6985C29.8922 68.7081 29.8976 68.6546 29.964 68.7107C29.9071 68.4162 30.0692 68.0015 30.0692 68.0015C30.1075 67.8378 30.1352 67.6625 30.1636 67.4915C30.1636 67.4915 30.3426 67.4035 30.5015 67.2396C30.6561 67.0763 30.7754 66.8259 30.6642 66.557C30.9548 66.3165 31.2027 66.0296 31.4701 65.7531C31.4701 65.7531 31.5814 65.2669 31.3596 65.0055C31.2237 64.8412 31.0365 64.6932 30.8464 64.5852C30.4586 64.3791 30.0661 64.323 30.0258 64.7153L30.0221 64.7202Z"
                            fill="#101214"
                        />
                        <path
                            d="M19.8985 52.2555C19.8822 52.2359 18.5192 52.5339 17.9191 52.886C17.9799 52.9955 17.8948 52.9027 17.8948 52.9027C17.6129 53.3834 16.9372 53.4964 16.9378 53.5007C15.6738 54.1968 13.8056 54.824 12.4939 55.7115L12.5102 55.7311L12.5134 55.7525C12.7179 55.8365 12.2241 56.0016 12.2651 55.8858C11.3645 55.9882 9.78125 56.6699 9.16929 57.3047C9.25581 57.4368 9.09549 57.5307 9.09097 57.4699C8.62575 57.2841 2.12179 59.8827 4.23465 60.4573L4.26943 60.6321C4.69021 60.5173 5.04295 60.4256 5.59108 60.5378C5.63719 60.4871 5.65914 60.5146 5.58693 60.3584C5.59123 60.3271 7.7304 59.5983 7.73421 59.6241C7.99919 59.9361 9.10708 58.2139 8.98115 59.1149C11.3958 59.4342 16.5482 56.4866 16.5482 56.4866C16.5726 56.5005 17.1919 55.7944 17.1919 55.7944L17.6255 55.2825C17.6255 55.2825 18.7052 55.0922 18.8742 54.4526L20.4055 53.963C20.4055 53.963 20.9486 53.5578 20.9965 53.1864C21.0247 52.9539 20.9972 52.7077 20.923 52.508C20.7683 52.0962 20.4123 51.8634 19.8923 52.2739L19.8985 52.2555Z"
                            fill="#101214"
                        />
                        <path
                            d="M23.324 73.7442C23.3478 73.7539 23.379 73.7537 23.4128 73.7706C23.3572 73.5154 23.5349 73.3882 23.7152 73.2781C23.8905 73.1644 24.1115 73.0879 24.211 72.9151C24.1059 72.8384 24.0376 72.7695 24.0958 72.8004L24.2178 72.9009C24.1294 72.4838 24.6397 72.7025 24.4016 72.1494L24.5375 72.3137C25.0444 71.6022 26.1217 70.9733 26.772 70.144L26.752 70.1294L26.74 70.1092C26.565 70.0429 26.0617 70.1436 26.0617 70.1436C25.8424 69.3243 26.6592 69.7129 26.9259 69.4628L27.0531 69.6283C26.924 69.7221 26.8713 69.8791 26.8778 69.9835C27.4991 69.8654 28.457 69.1838 28.6886 68.5745C28.5784 68.4635 28.6605 68.8377 28.303 68.4734C28.4102 68.383 28.4729 68.2332 28.5814 68.1513C28.7976 68.4047 28.6625 68.3676 28.6889 68.4252C28.8909 68.4919 30.0081 67.8307 30.7751 66.9755C30.962 66.7591 31.1314 66.5453 31.2621 66.3416C31.3872 66.1299 31.4698 65.9333 31.5075 65.7653C31.5786 65.4299 31.4282 65.2283 30.9684 65.2303L30.8513 65.1028C30.6087 65.2747 30.4123 65.4267 29.97 65.4262C29.9638 65.4754 29.9338 65.4535 30.0627 65.5705C30.0753 65.595 30.1004 65.644 30.1561 65.6884C30.4735 65.962 30.2841 65.9197 30.263 65.958C29.794 65.5049 29.6182 65.645 29.5413 65.911C29.4595 66.1733 29.4541 66.5604 29.3917 66.5916C28.9397 66.1623 29.1966 66.7521 28.9576 66.6162C28.9457 66.596 28.8735 66.4706 28.8616 66.4504C28.5524 66.202 28.4275 67.7746 28.1632 66.9839C28.1632 66.9839 23.2065 70.8026 23.1825 70.973C23.1825 70.973 23.0034 71.0609 22.8445 71.2249C22.69 71.3882 22.5706 71.6385 22.6818 71.9074C22.3913 72.1479 22.1433 72.4348 21.876 72.7114C21.876 72.7114 21.7647 73.1976 21.9865 73.4589C22.1224 73.6232 22.3095 73.7712 22.4997 73.8792C22.8875 74.0853 23.28 74.1414 23.3203 73.7492L23.324 73.7442Z"
                            fill="#101214"
                        />
                    </svg>
                </Box>
                <Box as="p" xcss={HeroSubheadingStyles}>
                    <FormattedMessage
                        id={messages.subheading.id}
                        defaultMessage={messages.subheading.defaultMessage}
                    />
                </Box>
            </Stack>
            <HeroTabs />
            <HeroCompanies />
        </Box>
    );
};

const HeroContainerStyle = xcss({
    marginInline: "auto",
    width: "100%",
    overflow: "hidden",
    paddingBlockEnd: "space.400"
});

const HeroHeadingWrapperStyles = xcss({
    position: "relative"
});

const HeroHeadingFigureStyles = css({
    position: "absolute",
    insetBlockStart: "40%",
    insetInlineEnd: "-11%",
    width: "72px",
    zIndex: 1,
    [media.above.xs]: {
        maxWidth: "420px"
    },
    [media.above.sm]: {
        maxWidth: "600px"
    },
    [media.above.lg]: {
        insetBlockStart: "54%",
        insetInlineEnd: "0"
    }
});

const HeroHeadingStyles = xcss({
    textAlign: "center",
    fontSize: "48px",
    fontWeight: "400",
    lineHeight: "56px",
    maxWidth: "378px",
    marginBlockEnd: "space.0",
    [media.above.sm]: {
        maxWidth: "600px"
    },
    [media.above.lg]: {
        fontSize: "70px",
        lineHeight: "86px",
        maxWidth: "800px"
    }
});

const HeroHeadingSpanStyles = xcss({
    fontWeight: "800"
});

const HeroSubheadingStyles = xcss({
    textAlign: "center",
    marginBlockEnd: "space.400",
    maxWidth: "328px",
    [media.above.xs]: {
        maxWidth: "420px"
    },
    [media.above.sm]: {
        maxWidth: "600px"
    },
    [media.above.lg]: {
        marginBlockEnd: "space.600",
        maxWidth: "800px",
        fontSize: "20px",
        lineHeight: "28px"
    }
});

export default JpdUseCaseProductTourOnWac;
